/**
 * The protocol's visual layouts — what the wizard's Design picker chooses
 * between. Only the look changes: every layout prints the same sections, the
 * same caveats and the same columns, so switching design can never change what
 * the record says.
 */
export const PROTOCOL_LAYOUTS = ["classic", "modern", "compact"] as const;

export type ProtocolLayout = (typeof PROTOCOL_LAYOUTS)[number];

export const DEFAULT_PROTOCOL_LAYOUT: ProtocolLayout = "classic";

const LAYOUT_STORAGE_KEY = "pm-protocol-layout";

export function isProtocolLayout(value: unknown): value is ProtocolLayout {
  return typeof value === "string" && (PROTOCOL_LAYOUTS as readonly string[]).includes(value);
}

/**
 * The layout this browser last exported with. A house style is chosen once and
 * reused, so it is remembered per viewer; anything unreadable (private mode,
 * cleared storage, a retired layout id) falls back to the default.
 */
export function readStoredProtocolLayout(): ProtocolLayout {
  try {
    const stored = window.localStorage.getItem(LAYOUT_STORAGE_KEY);
    return isProtocolLayout(stored) ? stored : DEFAULT_PROTOCOL_LAYOUT;
  } catch {
    return DEFAULT_PROTOCOL_LAYOUT;
  }
}

export function storeProtocolLayout(layout: ProtocolLayout): void {
  try {
    window.localStorage.setItem(LAYOUT_STORAGE_KEY, layout);
  } catch {
    // Storage unavailable: the choice still applies to this protocol, it is
    // just not remembered for the next one.
  }
}
